import { useMemo, useState } from 'react';
import { useQuery } from '@tanstack/react-query';
import { brandConfig } from '../../../config/brands';
import { apiClient } from '../../../services/apiClient';
import { useCreateProduct } from '../../../features/products/hooks';
import {
  draftToCreateRequest,
  emptyProductDraft,
  isProductDraftEmpty,
  newDraftId,
  type ProductDraft,
  type ProductDraftForm,
} from '../../../features/products/productDraft';
import { useToast } from '../../../hooks/useToast';
import { getErrorMessage } from '../../../utils/getErrorMessage';
import type { Product } from '../../../features/products/types';
import { CreateBrandModal } from '../../generation/scenario/CreateBrandModal';
import { ProductInfoEditTable } from '../../../pages/products/shared/ProductInfoEditTable';
import { BrandFieldHelp } from '../../../pages/products/shared/BrandFieldHelp';
import { DraftProductImagesSection } from '../../../pages/products/shared/DraftProductImagesSection';
import { useComplianceSettings } from '../../../features/compliance/api';
import { useConfirmDialog } from '../../common/useConfirmDialog';
// ManualProductDetailPage.tsx (frontend/src/pages/products/) と同じデザインにするため、
// 画像スロット・詳細テーブル・画像設定モーダルのCSSクラスをそのまま流用する。
import '../../../features/products/industry.css';
import '../../../pages/products/ProductDetailPage.css';

interface ManualProductFormModalProps {
  onClose: () => void;
  /** 登録成功時。createProduct レスポンスの product をそのまま返す (id / title / main_image_url を含む)。 */
  onRegistered: (product: Product) => void;
}

/**
 * マニュアル案件登録モーダル (素材パネルからの登録・計画書§4.1)。
 *
 * 2026-10-30 に「登録時に作る」方式へ切り替え (docs/features/manual-product-brand-first-flow.md §6.7)。
 * 開いた瞬間に空の案件を INSERT するのをやめ、文字項目と画像 (ストレージへ上げた URL) を下書き (ProductDraft) に持ち、
 * 「登録する」で 1 回の POST で作る。キャンセル / ✕ は下書きを捨てるだけで DELETE は要らない。
 * 入口の 2 択 (マニュアル / URL) はモーダルには設けない (2 段階は重い・同文書 §6.3 共通)。
 */
export function ManualProductFormModal({ onClose, onRegistered }: ManualProductFormModalProps) {
  const toast = useToast();
  const createProduct = useCreateProduct();
  const { data: complianceSettings } = useComplianceSettings();
  const productLabel = brandConfig.labels.productShort;

  const [draft, setDraft] = useState<ProductDraft>(() => emptyProductDraft(newDraftId()));
  const [validationErrors, setValidationErrors] = useState<Set<string>>(new Set());
  const [uploading, setUploading] = useState(false);
  // 破棄の確認はブラウザの confirm でなく独自モーダル (出ない環境がある・利用者指示 2026-10-30)
  const { confirm: confirmAction, dialog: confirmDialog } = useConfirmDialog();

  // ブランド一覧 (ManualProductDetailPage と同じクエリキー。CreateBrandModal 成功時の
  // invalidateQueries(['brands']) を共有し、作成直後のブランドをそのまま拾える)
  const { data: brandsData } = useQuery({
    queryKey: ['brands'],
    queryFn: async () => {
      const response = await apiClient.get('/brands');
      return response.data;
    },
    staleTime: 5 * 60 * 1000,
  });
  const brands = useMemo<{ id: string; brand_name: string }[]>(() => brandsData?.brands ?? [], [brandsData]);
  const [showCreateBrandModal, setShowCreateBrandModal] = useState(false);

  const updateForm = (form: ProductDraftForm) => setDraft((prev) => ({ ...prev, form }));

  // 下書きを捨てて閉じる (✕ / キャンセル共通)。案件行は無いので DELETE は要らない
  const handleDiscard = async () => {
    if (!isProductDraftEmpty(draft)) {
      const ok = await confirmAction({ title: '入力内容は保存されていません。破棄しますか？', confirmLabel: '破棄する', variant: 'warning' });
      if (!ok) return;
    }
    onClose();
  };

  const handleSave = async () => {
    const errors = new Set<string>();
    if (!draft.form.title.trim()) errors.add('title');
    setValidationErrors(errors);
    if (errors.size > 0) return;
    try {
      const created = await createProduct.mutateAsync(draftToCreateRequest(draft));
      toast.success(`${productLabel}を登録しました`);
      onRegistered(created);
    } catch (err) {
      toast.error(`登録に失敗しました: ${getErrorMessage(err)}`);
    }
  };

  const isSaving = createProduct.isPending;

  return (
    <div className="modal-overlay modal-overlay--nested">
      <div className="modal-card modal-card--xl">
        <div className="modal-header">
          <span className="modal-title">{productLabel}を登録</span>
          <button type="button" className="modal-close-button" aria-label="閉じる" onClick={() => void handleDiscard()} disabled={isSaving}>
            ✕
          </button>
        </div>
        <div className="modal-body">
          <section className="product-result">
            {/* 画像セクション（上部）: 下書きの枠。選んだ画像はストレージへ上げて URL を持つ */}
            <DraftProductImagesSection
              images={draft.images}
              onChange={(update) => setDraft((prev) => ({ ...prev, images: update(prev.images) }))}
              title={draft.form.title}
              disabled={isSaving}
              onUploadingChange={setUploading}
            />

            {/* 商品詳細テーブル（下部）。ブランド欄は先頭 (再構成計画 §2.4) */}
            <div className="product-details-only">
              <div className="product-details-header">
                <h2>{productLabel}情報</h2>
              </div>
              <table className="details-table">
                <tbody>
                  <ProductInfoEditTable
                    editForm={draft.form}
                    setEditForm={updateForm}
                    validationErrors={validationErrors}
                    brands={brands}
                    complianceSettings={complianceSettings}
                    onCreateBrand={() => setShowCreateBrandModal(true)}
                    brandFirst
                    brandHelp={<BrandFieldHelp />}
                  />
                </tbody>
              </table>
            </div>

            {/* フッタアクション: ボタン規約 (取り消し系=左 / 主アクション=右、space-between) */}
            <div className="bottom-actions bottom-actions--no-divider">
              <button type="button" className="btn-modal-secondary" onClick={() => void handleDiscard()} disabled={isSaving}>
                キャンセル
              </button>
              <button type="button" className="btn-modal btn-modal-sub" onClick={() => void handleSave()} disabled={isSaving || uploading} data-testid="manual-product-modal-submit">
                {isSaving ? '登録中...' : uploading ? '画像をアップロード中...' : '登録する'}
              </button>
            </div>
          </section>
        </div>
      </div>

      {/* ブランド新規作成 (入力中のフォーム内容はそのまま保持される)。作成したブランドを選択済みにする */}
      <CreateBrandModal
        isOpen={showCreateBrandModal}
        onClose={() => setShowCreateBrandModal(false)}
        onBrandCreated={(brandId, brand) => {
          const name = brand?.brand_name || brands.find((b) => b.id === brandId)?.brand_name || draft.form.brand_name;
          setDraft((prev) => ({ ...prev, form: { ...prev.form, brand_id: brandId, brand_name: name } }));
        }}
        confirmLabel={`登録して${productLabel}に紐づける`}
      />
      {confirmDialog}
    </div>
  );
}
